import { brandSchema, marketSchema } from 'data/schema';
import { getNewSessionData, userSession } from 'state/session';
import { maybeRevalidateDealerAccess, resetDealerAccess } from 'state/dealerAccess';
import { setStorageItem } from 'utils/ui/storage';

/**
 * Coordinates a dealer switch end-to-end and resolves only after the new session state is applied.
 *
 * Flow (mirrors the deprecated `User.dealerId` setter, but awaitable):
 * 1. Validate — brand (last letter) and market (first three letters) are parsed from the dealer id;
 *    an invalid id throws/rejects BEFORE any write, leaving storage and session untouched.
 * 2. Persist — write `auth:dealerId` to sessionStorage. This is the backing store of the
 *    `User.dealerId` getter, so the user object is consistent from this point on. It must happen
 *    BEFORE `getNewSessionData()`, which reads `user.dealerId` into the fresh session.
 * 3. Invalidate — clear the shared dealer-access decision (`resetDealerAccess()`) so no listener
 *    reacting to the session reset below can observe a stale grant/denial from the OLD dealer.
 *    This is the ONLY trigger that can see a sessionStorage-driven dealerId change: the
 *    `getUserInfoCallback` observer (dealerIdObserver.ts) keys off the identity callback's own
 *    dealer hint and structurally cannot see a write straight to sessionStorage.
 * 4. Reset — apply a fresh session with the new brand/market via `setValues({ reset: true })`.
 *    The reset applies and emits synchronously, so callers that await this function observe the
 *    fully switched session (a change in brand/market triggers re-routing in standalone).
 * 5. Re-check — `maybeRevalidateDealerAccess()` runs last, after the session reset, so the new
 *    dealer is checked automatically with no other caller involved: this is what remounts gated WC
 *    elements once the check resolves 'allowed'. This is deliberately NOT `ensureDealerAccess()`:
 *    that would (a) flip the `checksEnabled` latch on any switch, even before an authoritative entry
 *    point has run, which the latch's own contract forbids; (b) return the wrong promise — an old
 *    dealer's still-in-flight check — instead of starting a fresh check for the new dealer, if one
 *    was already running when the switch happened; and (c) break existing tests that call
 *    `selectDealer()` before any authoritative check has run and expect no request to be issued
 *    (e.g. `dealerAccess.test.ts`'s "is a no-op while checksEnabled is false").
 *    `maybeRevalidateDealerAccess()` respects the latch (a no-op before the first authoritative
 *    check) and, when a check is already in flight, sets `pendingRevalidation` so
 *    `ensureDealerAccess()`'s outer `.finally()` re-triggers once it settles.
 *
 * Dependency direction: this module statically imports `state/session` (which statically imports
 * `utils/auth/user`); `user.ts` may only reach this module via dynamic import to stay acyclic.
 * `state/dealerAccess` imports `utils/auth/user` but never this module, so importing
 * `resetDealerAccess`/`maybeRevalidateDealerAccess` here from `state/dealerAccess` stays a one-way
 * edge, no cycle.
 */
export async function selectDealer(dealerId: string): Promise<void> {
  // Extract brand (last letter) and market (first three letters) from dealer ID
  const brand = brandSchema.parse(dealerId.slice(-1));
  const market = marketSchema.parse(dealerId.slice(0, 3));

  setStorageItem('sessionStorage', 'auth:dealerId', dealerId);
  resetDealerAccess();

  userSession().setValues(
    {
      ...getNewSessionData(),
      brand,
      market,
    },
    { reset: true }
  );

  maybeRevalidateDealerAccess();
}
